import { PATHWAY } from 'services/coursewareItem/coursewareTypes';
import { enhanceElementMapWithState } from '../../configState/StateProvider/utils/enhanceElementMapWithState';
import { resolveWalkableResponse, resolveComponentResponse } from './graphQLResolvers';

// Turn a raw graphql 'get walkable' response into a map of
// the walkable itself, its components, and children references
export const parseWalkableData = async (walkable) => {
  let elementMap = {};

  // ACTIVITY or INTERACTIVE
  const resolvedWalkable = resolveWalkableResponse(walkable);
  elementMap[resolvedWalkable.id] = resolvedWalkable;

  // COMPONENTs
  if (walkable.components) {
    elementMap = walkable.components
      .reduce((acc, compRes) => {
        // Assume that the component is always registered to its parents scope, so look there for the matching URN
        const parentScope = resolvedWalkable.scope?.find(((scope) => scope.sourceId === compRes.id));
        const component = resolveComponentResponse(compRes, parentScope?.scopeURN);

        // Component ancestry isn't provided by the server request, so we build it manually for later use.
        component.ancestry = [
          {
            elementId: resolvedWalkable.id,
            elementType: resolvedWalkable.elementType,
          },
          ...resolvedWalkable.ancestry,
        ];

        return {
          ...acc,
          [compRes.id]: component,
        };
      }, elementMap);
  }

  // PATHWAYs and PATHWAY children (only relevant for ACTIVITY walkables)
  // For children we only need a simple map `id:{ id, elementType, ancestry }` since
  // we just use this as a shortcut for determining the element type.
  if (resolvedWalkable.pathways) {
    elementMap = resolvedWalkable.pathways
      .flatMap((pathway) => (
        pathway.walkables.map((childWalkable) => ({
          id: childWalkable.id,
          elementType: childWalkable.elementType,
          // Child walkable ancestries aren't provided by the server request, so we build it manually for later use.
          ancestry: [
            {
              elementId: pathway.id || pathway.elementId,
              elementType: PATHWAY,
            },
            {
              elementId: resolvedWalkable.id,
              elementType: resolvedWalkable.elementType,
            },
            ...resolvedWalkable.ancestry,
          ],
        }))
      ))
      .reduce((acc, childWalkable) => ({ ...acc, [childWalkable.id]: childWalkable }), elementMap);
  }

  try {
    // For backwards compatibility we add state to our elementMap. Ideally this should be a separate call
    elementMap = await enhanceElementMapWithState(elementMap, walkable);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }

  // Some data (pathways) should be removed from the result.
  elementMap = pruneExtraneousElements(elementMap);

  return elementMap;
};

const pruneExtraneousElements = (elementMap) => Object.entries(elementMap).reduce((acc, [elementId, { pathways, ...rest }]) => {
  const element = {
    ...rest,
  };
  return ({
    ...acc,
    [elementId]: element,
  });
}, {});
